import { createSlice } from '@reduxjs/toolkit';
import api from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const sessionsSlice = createSlice({
    name: 'sessions',
    initialState: {
        pending: false,
        savePending: false,
        sessions: [],
        error: null,
        deletePending: false,
        deleteError: null,
        deleteId: null,
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        startDelete: (state, action) => {
            state.deletePending = true;
            state.deleteId = action.payload;
        },
        stopDelete: (state, action) => {
            state.deletePending = false;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        addSession: (state, action) => {
            state.savePending = false;
            state.sessions = [action.payload, ...state.sessions];
            state.total = state.total + 1;
        },
        editSession: (state, action) => {
            state.savePending = false;
            const index = state.sessions.findIndex((session) => session.id === action.payload.id);
            if (index !== -1) {
                state.sessions = [
                    ...state.sessions.slice(0, index),
                    action.payload,
                    ...state.sessions.slice(index + 1),
                ];
            }
        },
        deleteSession: (state, action) => {
            state.deletePending = false;
            const index = state.sessions.findIndex((session) => session.id === action.payload.id);
            if (index !== -1) {
                const wasAlreadyCancelled = state.sessions[index].status === 'cancelled';
                state.sessions[index] = {
                    ...state.sessions[index],
                    status: 'cancelled',
                };
                if (!wasAlreadyCancelled) {
                    state.total = Math.max(0, state.total - 1);
                }
            }
        },
        setSessions: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.sessions = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
    },
});

export const {
    start,
    stop,
    setSessions,
    stopDelete,
    setError,
    addSession,
    editSession,
    deleteSession,
    startDelete,
    startSave,
    stopSave,
    setSaveError,
} = sessionsSlice.actions;

export const getSessions = (state) => state.sessions.sessions;
export const getSessionsPending = (state) => state.sessions.pending;
export const getSessionsSavePending = (state) => state.sessions.savePending;
export const getSessionsDeletePending = (state) => state.sessions.deletePending;
export const getSessionsDeleteId = (state) => state.sessions.deleteId;
export const getSessionsError = (state) => state.sessions.error;
export const getSessionsTotal = (state) => state.sessions.total;
export const getSessionsCurrentPage = (state) => state.sessions.currentPage;
export const getSessionsTotalPages = (state) => state.sessions.totalPages;

export const fetchSessionsAction =
    ({ filters, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(filters);
            dispatch(start());
            api.get('user-sessions' + query)
                .then((response) => {
                    dispatch(setSessions(response.data));
                    if (onSuccess) {
                        onSuccess(response.data.items);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error(e.message);
        }
    };

export const updateSessionStatusAction = (sessionId, status, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(startSave());

        // Mock response for testing - remove this when API is ready
        const mockUpdatedSession = {
            id: sessionId,
            inviteeName: 'John Doe',
            inviteeEmail: 'john@example.com',
            status: status,
            type: 'one_to_one',
            startDate: new Date(),
            duration: 30,
            createdAt: new Date(),
        };

        dispatch(editSession(mockUpdatedSession));
        if (onSuccess) {
            onSuccess(null);
        }

        // Uncomment this when API is ready
        /*
        api.put(`sessions/${sessionId}/status`, { status })
            .then((response) => {
                dispatch(editSession(response.data));
                if (onSuccess) {
                    onSuccess(null);
                }
                return response;
            })
            .catch((error) => {
                dispatch(setSaveError(error));
                if (onFailure) {
                    onFailure(error);
                }
            });
        */
    } catch (e) {
        dispatch(stopSave());
        return console.error('error', e);
    }
};

export const updateSessionGroupAction = (sessionId, userGroupId, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(startSave());

        api.put(`user-sessions/${sessionId}`, { userGroupId })
            .then((response) => {
                dispatch(editSession(response.data));
                if (onSuccess) {
                    onSuccess(null);
                }
                return response;
            })
            .catch((error) => {
                dispatch(setSaveError(error));
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        dispatch(stopSave());
        return console.error('error', e);
    }
};

export const deleteSessionAction =
    ({ id, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startDelete(id));
            api.put('user-sessions/cancel/' + id)
                .then((response) => {
                    dispatch(deleteSession({ id }));
                    dispatch(stopDelete());
                    if (onSuccess) {
                        onSuccess(null);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    dispatch(stopDelete());
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stopDelete());
            return console.error('error', e);
        }
    };

export const inviteUserAction =
    ({ userDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            api.post('user-sessions', userDto).then((response) => {
                dispatch(addSession(response.data));
                if (onSuccess) {
                    onSuccess(response.data);
                }
            });
        } catch (e) {
            dispatch(stopSave());
            return console.error('error', e);
        }
    };

export default sessionsSlice.reducer;
